import { Note } from '@/types/note';
import { confirm } from '@/components/ui/confirmation-dialog';
import { CONTENT_MAX_WIDTH, centeredContent } from '@/components/layout/content-width';
import { renderKeyboardAwareScroll } from '@/components/layout/keyboard-aware-screen-scroll';
import { ScreenHeader } from '@/components/layout/screen-header';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Text } from '@/components/ui/text';
import { useLikeCheckIn } from '@/features/activity/hooks/use-like-check-in';
import { useFriendActions } from '@/features/friends/hooks/use-friend-actions';
import { useFriendshipStatus } from '@/features/friends/hooks/use-friendship-status';
import { useUserFriends } from '@/features/friends/hooks/use-user-friends';
import { listsQueryOptions } from '@/api/queries/lists';
import { ActivityCard } from '@/features/activity/components/activity-card';
import { ListCard } from '@/features/lists/components/list-card';
import { NotesList } from '@/features/notes/components';
import { useDeleteNote, useUpdateNote } from '@/features/notes/hooks/use-note-mutations';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useAppTheme } from '@/providers/theme-provider';
import type { FeedItem } from '@/types/feed';
import type { Friend } from '@/types/friendship';
import { User } from '@/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { router } from 'expo-router';
import React, { useCallback, useMemo, useState } from 'react';
import { ActivityIndicator, FlatList, Pressable, RefreshControl, View } from 'react-native';
import { FriendRow } from './components/friend-row';
import { FriendshipBadge } from './components/friendship-badge';
import { ProfileActions } from './components/profile-actions';
import { ProfileHeader } from './components/profile-header';
import { QrCodeModal } from './components/qr-code-modal';
import { ProfileStats } from './components/profile-stats';
import { TasteBar } from './components/taste-bar';
import { useProfileFeed } from './hooks/use-profile-feed';
import { useProfileTaste } from './hooks/use-profile-taste';

type ProfileTab = 'activity' | 'friends' | 'lists' | 'notes';

interface ProfileScreenProps {
  user: User;
  isCurrentUser?: boolean;
  /** Header title for other users' profiles (ignored for the current user). */
  title?: string;
  /** Pull-to-refresh handler; when omitted the list has no RefreshControl. */
  onRefresh?: () => void;
  refreshing?: boolean;
  /** Extra bottom padding (e.g. tab bar clearance) applied to the scroll content. */
  bottomInset?: number;
}

export function ProfileScreen({
  user,
  isCurrentUser = false,
  title,
  onRefresh,
  refreshing = false,
  bottomInset = 0,
}: ProfileScreenProps) {
  const [qrVisible, setQrVisible] = useState(false);
  const [activeTab, setActiveTab] = useState<ProfileTab>('activity');
  const theme = useAppTheme();
  const queryClient = useQueryClient();
  const {
    data,
    isLoading: feedLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useProfileFeed(user.id);

  const { toggleLike } = useLikeCheckIn();
  // Friends stays eager: its count feeds the always-visible ProfileStats band,
  // so gating it would zero the "Friends" stat until the tab is opened.
  const { data: friendsData, isLoading: friendsLoading } = useUserFriends(user.id);
  const { data: taste } = useProfileTaste(user.id);

  const { data: friendshipData } = useFriendshipStatus(user.id, {
    enabled: !isCurrentUser,
  });
  const { removeFriend } = useFriendActions();
  const deleteNoteMutation = useDeleteNote();
  const updateNoteMutation = useUpdateNote();
  // Lists renders only inside the Lists tab and is not summarised in the header,
  // so defer it until that tab is active -- it was the one query loading on
  // mount for a tab the user may never open. Prefetched on tab press below.
  const listsOptions = useMemo(() => listsQueryOptions({ per_page: 20 }), []);
  const { data: listsData, isLoading: listsLoading } = useQuery({
    ...listsOptions,
    enabled: isCurrentUser && activeTab === 'lists',
  });
  const userLists = useMemo(() => listsData?.items ?? [], [listsData]);

  const isFriend = friendshipData?.status === 'friends';

  // Warm the target tab's data as the user taps, so switching feels instant
  // instead of showing a spinner on first open.
  const handleTabChange = useCallback(
    (next: ProfileTab) => {
      if (next === 'lists' && isCurrentUser) {
        queryClient.prefetchQuery(listsOptions);
      }
      setActiveTab(next);
    },
    [isCurrentUser, queryClient, listsOptions],
  );

  const handleDeleteNote = useCallback(async (note: Note) => {
    const confirmed = await confirm({
      title: 'Delete this note?',
      description: 'Are you sure you want to delete this note? This cannot be undone.',
      variant: 'destructive',
      confirmLabel: 'Delete',
    });
    if (confirmed) {
      deleteNoteMutation.mutate(note.id);
    }
  }, [deleteNoteMutation]);

  const handleSaveNote = useCallback((noteId: string, content: string) => {
    updateNoteMutation.mutate({ id: noteId, data: { content } });
  }, [updateNoteMutation]);

  const handleRemoveFriend = useCallback(async () => {
    const confirmed = await confirm({
      title: `Unfriend ${user.name}?`,
      description: `You'll stop seeing each other's check-ins and lists. You can always send a new friend request later.`,
      variant: 'destructive',
      confirmLabel: 'Remove friend',
    });
    if (confirmed) {
      removeFriend.mutate(user.id);
    }
  }, [removeFriend, user.id, user.name]);

  const friendshipBadge = (!isCurrentUser && isFriend) ? (
    <FriendshipBadge onManage={handleRemoveFriend} isRemoving={removeFriend.isPending} />
  ) : undefined;

  const items = useMemo(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data],
  );

  const checkInCount = data?.pages?.[0]?.total ?? 0;
  const friendsCount = friendsData?.length ?? 0;
  const mustTryCount = taste?.distribution.mustTry ?? 0;

  // The single virtualized list is driven by the active tab. Only the active
  // tab's items are mounted, so a heavy profile no longer keeps every card +
  // image in memory the way the old `.map()` inside a ScrollView did.
  const listData = useMemo<readonly unknown[]>(() => {
    switch (activeTab) {
      case 'activity':
        return items;
      case 'friends':
        return friendsData ?? [];
      case 'lists':
        return isCurrentUser ? userLists : [];
      case 'notes':
      default:
        return []; // Notes render via NotesList in the footer (self-paginating).
    }
  }, [activeTab, items, friendsData, isCurrentUser, userLists]);

  const renderItem = useCallback(
    ({ item }: { item: unknown }) => {
      switch (activeTab) {
        case 'activity':
          return (
            <ActivityCard item={item as FeedItem} onLike={toggleLike} />
          );
        case 'friends':
          return <FriendRow friend={item as Friend} />;
        case 'lists':
          return (
            <ListCard
              list={item as (typeof userLists)[number]}
              onPress={() =>
                router.push({
                  pathname: '/(root)/lists/[id]',
                  params: { id: (item as { id: string }).id },
                })
              }
            />
          );
        default:
          return null;
      }
    },
    [activeTab, toggleLike],
  );

  const keyExtractor = useCallback(
    (item: unknown, index: number) => {
      if (activeTab === 'activity') {
        return (item as FeedItem).checkIn.id;
      }
      return (item as { id?: string }).id ?? String(index);
    },
    [activeTab],
  );

  const handleEndReached = useCallback(() => {
    if (activeTab === 'activity' && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [activeTab, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const listHeader = (
    <View>
      <ProfileHeader user={user} friendshipBadge={friendshipBadge} />
      <ProfileStats
        checkInCount={checkInCount}
        friendsCount={friendsCount}
        mustTryCount={mustTryCount}
      />
      <TasteBar taste={taste} />
      <ProfileActions isCurrentUser={isCurrentUser} userId={user.id} />
      <Tabs value={activeTab} onValueChange={(v) => handleTabChange(v as ProfileTab)}>
        {/* Up to four equal-width tabs share the phone-width row. They drop the
            shared list's leading indent and step down to the 14px Label size so
            "Activity"/"Friends" fit on one line instead of truncating at 16px. */}
        <TabsList className="ps-0 gap-2">
          <TabsTrigger value="activity" title="Activity" className="px-2" textClassName="text-sm" />
          <TabsTrigger value="friends" title="Friends" className="px-2" textClassName="text-sm" />
          {isCurrentUser && <TabsTrigger value="lists" title="Lists" className="px-2" textClassName="text-sm" />}
          {isCurrentUser && <TabsTrigger value="notes" title="Notes" className="px-2" textClassName="text-sm" />}
        </TabsList>
      </Tabs>
      <View className="h-2" />
    </View>
  );

  const listEmpty = useMemo(() => {
    if (activeTab === 'activity') {
      if (feedLoading) {
        return (
          <View className="py-8 items-center">
            <ActivityIndicator color={theme.colors.primary} />
          </View>
        );
      }
      return <EmptyState icon="restaurant-outline" label="No check-ins yet" />;
    }
    if (activeTab === 'friends') {
      if (friendsLoading) {
        return (
          <View className="py-8 items-center">
            <ActivityIndicator color={theme.colors.primary} />
          </View>
        );
      }
      return <EmptyState icon="people-outline" label="No friends yet" />;
    }
    if (activeTab === 'lists') {
      if (listsLoading) {
        return (
          <View className="py-8 items-center">
            <ActivityIndicator color={theme.colors.primary} />
          </View>
        );
      }
      return (
        <View className="items-center py-8 px-4">
          <Text className="text-sm text-muted-foreground text-center">
            No lists yet. Save menu items and restaurants to organize them.
          </Text>
          <Pressable
            onPress={() => router.push('/(root)/lists' as never)}
            className="mt-3"
            accessibilityRole="button"
            accessibilityLabel="Go to Lists"
            hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
          >
            <Text className="text-sm text-primary font-medium">Go to Lists</Text>
          </Pressable>
        </View>
      );
    }
    return null; // notes: NotesList owns its own empty/loading UI
  }, [activeTab, feedLoading, friendsLoading, listsLoading, theme.colors.primary]);

  const listFooter = useMemo(() => {
    if (activeTab === 'activity') {
      if (isFetchingNextPage) {
        return (
          <View className="py-4 items-center">
            <ActivityIndicator color={theme.colors.primary} />
          </View>
        );
      }
      return null;
    }
    if (activeTab === 'lists' && userLists.length > 0) {
      return (
        <Pressable
          onPress={() => router.push('/(root)/lists' as never)}
          className="items-center py-3"
          accessibilityRole="button"
          accessibilityLabel="View all lists"
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          <Text className="text-sm font-medium text-primary">View all lists</Text>
        </Pressable>
      );
    }
    if (activeTab === 'notes') {
      return (
        <NotesList
          userId={user.id}
          onSave={handleSaveNote}
          onDelete={handleDeleteNote}
          nested
        />
      );
    }
    return null;
  }, [
    activeTab,
    isFetchingNextPage,
    theme.colors.primary,
    userLists.length,
    user.id,
    handleSaveNote,
    handleDeleteNote,
  ]);

  const headerRightSlot = (
    <View className="flex-row items-center" style={{ gap: 16 }}>
      {user.username && (
        <Pressable
          onPress={() => setQrVisible(true)}
          accessibilityRole="button"
          accessibilityLabel="Share profile QR code"
          hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
        >
          <Ionicons name="qr-code-outline" size={22} className="text-foreground" />
        </Pressable>
      )}
      {isCurrentUser && (
        <Pressable
          onPress={() => router.push({ pathname: '/(tabs)/profile/edit' })}
          accessibilityRole="button"
          accessibilityLabel="Edit Profile"
          hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
        >
          <Text className="text-sm font-medium text-primary">Edit Profile</Text>
        </Pressable>
      )}
    </View>
  );

  return (
    <View className="flex-1">
      <ScreenHeader
        title={isCurrentUser ? 'Profile' : title ?? user.name ?? 'Profile'}
        showMenuButton={isCurrentUser}
        showBackButton={!isCurrentUser}
        rightSlot={headerRightSlot}
      />
      <FlatList
        className="flex-1"
        data={listData}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        ListHeaderComponent={listHeader}
        ListEmptyComponent={listEmpty}
        ListFooterComponent={listFooter}
        onEndReached={handleEndReached}
        onEndReachedThreshold={0.5}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        renderScrollComponent={renderKeyboardAwareScroll}
        contentContainerStyle={{
          paddingHorizontal: 24,
          paddingTop: 24,
          paddingBottom: 24 + bottomInset,
          ...centeredContent(CONTENT_MAX_WIDTH),
        }}
        refreshControl={
          onRefresh ? (
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          ) : undefined
        }
      />
      {user.username && (
        <QrCodeModal
          visible={qrVisible}
          onClose={() => setQrVisible(false)}
          username={user.username}
          displayName={user.name}
        />
      )}
    </View>
  );
}

function EmptyState({ icon, label }: { icon: 'restaurant-outline' | 'people-outline'; label: string }) {
  return (
    <View className="items-center py-8">
      <View className="w-16 h-16 rounded-full items-center justify-center mb-4 bg-surface-secondary">
        <Ionicons name={icon} size={28} className="text-muted-foreground" />
      </View>
      <Text className="text-sm text-center text-muted-foreground">{label}</Text>
    </View>
  );
}
